import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";

// @material-ui/icons
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import Edit from "@material-ui/icons/Edit";
import Archive from "@material-ui/icons/Archive";
import Unarchive from "@material-ui/icons/Unarchive";
import Visibility from "@material-ui/icons/Visibility";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";

// Core components
import Button from "components/CustomButtons/Button.js";

import styles from "assets/jss/material-dashboard-pro-react/components/tableStyle.js";

const CategoryTable = ({ classes, categories, loading, expandedCategories, onToggleExpand, onEdit, onArchive, onView, activeStoreObject }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);

  // Helper function to check if a category is geo-restricted for the current store
  const isCategoryGeoRestricted = (category) => {
    if (!activeStoreObject?.state || !category.mp_georestriction) {
      return false;
    }
    return Array.isArray(category.mp_georestriction) && 
           category.mp_georestriction.includes(activeStoreObject.state);
  };

  const handleMenuClick = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleToggleExpand = (categoryId) => {
    onToggleExpand(categoryId);
  };

  const handleAction = (action) => {
    if (!selectedCategory) return;
    
    switch (action) {
      case 'view':
        onView(selectedCategory);
        break;
      case 'edit':
        onEdit(selectedCategory);
        break;
      case 'archive':
        onArchive(selectedCategory, false);
        break;
      case 'unarchive':
        onArchive(selectedCategory, true);
        break;
    }
    handleMenuClose();
  };

  const getPlatformIcon = (category, platform) => {
    const isSupported = category.platforms && category.platforms.includes(platform);
    return (
      <span style={{ color: isSupported ? '#4caf50' : '#f44336', fontSize: '18px' }}>
        {isSupported ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
      </span>
    );
  };

  const renderCategoryRow = (category, depth = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category._id);
    const paddingLeft = depth * 30; // Increased for better visual separation
    const isGeoRestricted = isCategoryGeoRestricted(category);
    
    // Add visual depth indicator for deeper levels
    const depthIndicator = depth > 0 ? (
      <span 
        style={{ 
          marginRight: '8px', 
          color: '#999', 
          fontSize: '11px',
          fontWeight: 'normal',
          opacity: 0.7
        }}
      >
        {'└'.repeat(depth)}
      </span>
    ) : null;

    return (
      <React.Fragment key={category._id}>
        <TableRow 
          className={classes.tableBodyRow}
          style={{
            backgroundColor: isGeoRestricted ? '#ffebee' : 'inherit', // Light red background for geo-restricted categories
            borderLeft: isGeoRestricted ? '4px solid #f44336' : 'none' // Red border accent
          }}
        >
          <TableCell className={classes.tableCell} style={{ verticalAlign: 'middle' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              paddingLeft: `${paddingLeft}px`
            }}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={() => handleToggleExpand(category._id)}
                  style={{ 
                    marginRight: '8px', 
                    padding: '2px', 
                    width: '24px',
                    height: '24px',
                    backgroundColor: isExpanded ? '#f44336' : '#4caf50',
                    color: 'white',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: isExpanded ? '#d32f2f' : '#388e3c'
                    }
                  }}
                >
                  {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                </IconButton>
              ) : (
                <div style={{ width: '32px', marginRight: '8px' }} />
              )}
              {category.icon && (
                <img 
                  src={category.icon} 
                  alt={category.name}
                  style={{ width: '30px', height: '30px', marginRight: '10px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span style={{ 
                color: category.enabled === false ? '#f44336' : 'inherit',
                fontWeight: category.enabled === false ? 'normal' : '500'
              }}>
                {category.name}
              </span>
            </div>
          </TableCell>
          <TableCell className={classes.tableCell}>
            {getPlatformIcon(category, 'ios')}
          </TableCell>
          <TableCell className={classes.tableCell}>
            {getPlatformIcon(category, 'android')}
          </TableCell>
          <TableCell className={classes.tableCell}>
            {getPlatformIcon(category, 'web')}
          </TableCell>
          <TableCell className={classes.tableCell}>
            <Chip
              label={category.enabled === false ? 'Archived' : 'Active'}
              color={category.enabled === false ? 'secondary' : 'primary'}
              size="small"
              variant="outlined"
            />
          </TableCell>
          <TableCell className={classes.tableCell}>
            <IconButton
              onClick={(e) => handleMenuClick(e, category)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          </TableCell>
        </TableRow>
        {hasChildren && isExpanded && (
          category.children.map(child => renderCategoryRow(child, depth + 1))
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.tableResponsive}>
      <Table className={classes.table}>
        <TableHead className={classes.primaryTableHeader}>
          <TableRow>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              Category Name
            </TableCell>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              iOS
            </TableCell>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              Android
            </TableCell>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              Web
            </TableCell>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              Status
            </TableCell>
            <TableCell className={classes.tableCell + " " + classes.tableHeadCell}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map(category => renderCategoryRow(category))}
        </TableBody>
      </Table>

      {categories.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No categories found
        </div>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <Visibility style={{ marginRight: '8px' }} />
          View
        </MenuItem>
        <MenuItem onClick={() => handleAction('edit')}>
          <Edit style={{ marginRight: '8px' }} />
          Edit
        </MenuItem>
        {selectedCategory && selectedCategory.enabled !== false ? (
          <MenuItem onClick={() => handleAction('archive')}>
            <Archive style={{ marginRight: '8px' }} />
            Archive
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleAction('unarchive')}>
            <Unarchive style={{ marginRight: '8px' }} />
            Unarchive
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

CategoryTable.propTypes = {
  classes: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  expandedCategories: PropTypes.instanceOf(Set).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  activeStoreObject: PropTypes.object,
};

export default connect(
  (state) => ({
    activeStoreObject: state.app.activeStoreObject,
  }),
  {}
)(withStyles(styles)(CategoryTable));
